import classNames from 'classnames';
import { useGridPatchedCells } from 'components/common-form/hooks/patched-fields/useGridPatchedCells';
import { useData } from 'components/common-form/hooks/useData';
import { usePasteGridData } from 'hooks/usePasteGridData';
import { isEqual } from 'lodash-es';
import type { GridCellsMetadata, GridData, GridFieldHeader } from 'models/forms/fields';
import { DragEvent, FC, MouseEvent, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { mediaQueryDown } from 'utils/mediaQueries';

import { GridCell } from './GridCell';
import { buildCellId, buildCellIds, clippingWithGrid, nextCellCoordinates } from './GridField.helper';
import type { CellCoordinates, Navigation } from './GridField.model';
import { GridHeaders } from './GridHeaders';

const FitContentDiv = styled.div`
  ${mediaQueryDown('md')`width: fit-content;`}
`;

const StyledTd = styled.td<{ $canFitDatePickerPopup: boolean }>`
  min-width: ${props => (props.$canFitDatePickerPopup ? '360px' : 'inherit')};
`;

const StyledDragHandle = styled.div`
  z-index: 100;
  cursor: cell;
  width: 8px;
  height: 8px;
`;

type GridWithoutPaginationProps = {
  id: string;
  hasRowIndexColumn: boolean;
  rowsMetadata: GridCellsMetadata;
  headers: readonly GridFieldHeader[];
  nbColumns: number;
  tabIndex?: number;
};

export const GridWithoutPagination: FC<GridWithoutPaginationProps> = ({
  id,
  hasRowIndexColumn,
  rowsMetadata,
  headers,
  nbColumns,
  tabIndex,
}) => {
  const [gridData, setGridData] = useData<GridData>(id);
  const nbRows = gridData.length;

  const onCellValueChanged = useCallback(
    (row: number, cellId: string, value: unknown) => {
      const newRow: Record<string, unknown> = { ...gridData[row], [cellId]: value, isEdited: true };
      const newGridData: GridData = [...gridData.slice(0, row), newRow, ...gridData.slice(row + 1)];
      setGridData(newGridData);
    },
    [gridData, setGridData],
  );

  const onPaste = usePasteGridData({ gridData, headers, setGridData });

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const [selectedCellIds, setSelectedCellIds] = useState<string[]>([]);

  const onCellSelected = useCallback((cellId: string) => {
    setSelectedCellIds(new Array(cellId));
    setDragHandleCellId(cellId);
  }, []);

  const onNavigated = useCallback(
    (origin: CellCoordinates, navigation: Navigation, newIsEditing: boolean = false) => {
      const clipping = clippingWithGrid({
        gridId: id,
        nbRows,
        nbColumns,
      });
      const nextCoordinates = nextCellCoordinates(origin, navigation, clipping);
      const nextCellId = buildCellId(id, nextCoordinates.column, nextCoordinates.row);

      setSelectedCellIds(new Array(nextCellId));
      setDragHandleCellId(nextCellId);
      setIsEditing(() => newIsEditing);
    },
    [id, nbColumns, nbRows],
  );

  const onEditingChanged = useCallback((newIsEditing: boolean) => setIsEditing(() => newIsEditing), []);

  const [dragCell, setDragCell] = useState<CellCoordinates | undefined>(undefined);

  const [dropCell, setDropCell] = useState<CellCoordinates | undefined>(undefined);

  const [dragHandleCellId, setDragHandleCellId] = useState<string | undefined>(undefined);

  const handleDragStart = useCallback((e: DragEvent, rowIndex: number, columnIndex: number) => {
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
    e.dataTransfer.setDragImage(img, 0, 0);
    setDragCell({ row: rowIndex, column: columnIndex });
  }, []);

  const handleDragOver = useCallback(
    (e: MouseEvent, rowIndex: number, columnIndex: number) => {
      if (dragCell && dragCell.column === columnIndex) {
        e.preventDefault();
        setSelectedCellIds(buildCellIds(id, dragCell.row, dragCell.column, rowIndex, columnIndex));
        setDropCell({ row: rowIndex, column: columnIndex });
      } else {
        setSelectedCellIds([]);
        setDropCell(undefined);
        setDragHandleCellId(undefined);
      }
    },
    [dragCell, id],
  );

  const handleDragToFill = useCallback(
    (cellId: string) => {
      if (dragCell !== undefined && dropCell !== undefined) {
        let isChanged = false;
        const newGridData = [...gridData];
        const value = gridData[dragCell.row][cellId];
        const startRow = Math.min(dragCell.row, dropCell.row);
        const endRow = Math.max(dragCell.row, dropCell.row);
        for (let row = startRow; row <= endRow; row++) {
          if (!isEqual(newGridData[row][cellId], value)) {
            newGridData[row][cellId] = value;
            isChanged = true;
          }
        }
        if (isChanged) {
          setGridData(newGridData);
        }
      }
    },
    [dragCell, dropCell, gridData, setGridData],
  );

  const handleDrop = useCallback(
    (fieldId: string) => {
      handleDragToFill(fieldId);
      setDragHandleCellId(selectedCellIds[selectedCellIds.length - 1]);
    },
    [handleDragToFill, selectedCellIds],
  );

  const isSelected = useCallback(
    (cellId: string) => {
      return selectedCellIds.includes(cellId);
    },
    [selectedCellIds],
  );

  const showDragHandle = useCallback(
    (cellId: string) => {
      return cellId === dragHandleCellId;
    },
    [dragHandleCellId],
  );

  const isScheduleGrid = useMemo(() => ['ScheduleDates', 'ExerciseSchedule', 'SwapSchedule'].includes(id), [id]);

  const getCellHighlight = useGridPatchedCells(id);

  return (
    <div>
      <FitContentDiv data-e2e={id}>
        <table className="table table-form table-bordered table-thead-bordered m-0" data-e2e={'editable-grid-' + id}>
          <GridHeaders id={id} headers={headers} hasRowIndexColumn={hasRowIndexColumn} />
          <tbody>
            {gridData.map((line, rowIndex) => {
              return (
                <tr key={rowIndex}>
                  {hasRowIndexColumn && (
                    <th scope="row" className="align-middle text-center text-secondary p-0">
                      {rowIndex + 1}
                    </th>
                  )}
                  {headers.map((cellMetadata, columnIndex) => (
                    <StyledTd
                      key={`${cellMetadata.id}.${rowIndex + 1}`}
                      className={classNames(
                        'p-0 align-bottom position-relative',
                        getCellHighlight(rowIndex, cellMetadata.id),
                      )}
                      $canFitDatePickerPopup={cellMetadata.type === 'DatesListField'}
                      onDragOver={(e: DragEvent) => handleDragOver(e, rowIndex, columnIndex)}
                      onDrop={() => handleDrop(cellMetadata.id)}
                    >
                      {showDragHandle(buildCellId(id, columnIndex, rowIndex)) &&
                        cellMetadata.isReadOnly !== true &&
                        isScheduleGrid && (
                          <StyledDragHandle
                            className="bg-info rounded-full position-absolute bottom-0 end-0 d-block cursor-cell"
                            data-e2e={`drag-handle`}
                            draggable={true}
                            onDragStart={(e: DragEvent) => handleDragStart(e, rowIndex, columnIndex)}
                          />
                        )}
                      <GridCell
                        gridId={id}
                        isEditing={isEditing}
                        cellId={buildCellId(id, columnIndex, rowIndex)}
                        isSelected={isSelected(buildCellId(id, columnIndex, rowIndex))}
                        columnIndex={columnIndex}
                        rowIndex={rowIndex}
                        metadata={cellMetadata}
                        value={line?.[cellMetadata.id]}
                        onValueChanged={onCellValueChanged}
                        onPaste={onPaste}
                        onCellSelected={onCellSelected}
                        onNavigated={onNavigated}
                        setIsEditing={onEditingChanged}
                        isDraggable={id === 'ScheduleDates'}
                        tabIndex={tabIndex}
                        customizedData={rowsMetadata?.[rowIndex]?.[cellMetadata.id]}
                      />
                    </StyledTd>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </FitContentDiv>
    </div>
  );
};

GridWithoutPagination.displayName = 'GridWithoutPagination';
